import type { Brand } from '@seek/melways-sites';
import { Head } from '@unhead/react';
import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonLink,
  Heading,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';

import { brandNameMapper } from '../../../utils/brandNameMapper';

import translations from './.vocab';
import sky from './images/sky.svg';

import { profileNotAvailableBlurBackground } from './ProfileNotAvailable.css';

interface ProfileNotAvailableProps {
  brand: Brand;
  updateVisibilityLink: string;
  searchLink: string;
}

export const ProfileNotAvailable = ({
  brand,
  updateVisibilityLink,
  searchLink,
}: ProfileNotAvailableProps) => {
  const { t } = useTranslations(translations);

  const noLinkDescription = t(
    'THIS_PROFILE_IS_PRIVATE_OR_NOT_CREATED_CHECK_VISIBILITY_SETTINGS',
    {
      Link: (children) => children,
    },
  )?.toString();

  return (
    <>
      <Head>
        <meta name="description" content={noLinkDescription} />
        <title>
          {t('THIS_PROFILE_IS_NOT_AVAILABLE_BRAND', {
            brand: brandNameMapper[brand],
          })}
        </title>
      </Head>
      <Box className={profileNotAvailableBlurBackground} paddingX="small">
        <Box style={{ margin: 'auto' }} maxWidth="xsmall">
          <Stack space="large" align={{ mobile: 'center' }}>
            <Box dangerouslySetInnerHTML={{ __html: sky }} />
            <Heading level="2" weight="regular">
              {t('THIS_PROFILE_IS_NOT_AVAILABLE')}
            </Heading>
            <Text>
              {t(
                'THIS_PROFILE_IS_PRIVATE_OR_NOT_CREATED_CHECK_VISIBILITY_SETTINGS',
                {
                  Link: (children) => (
                    <TextLink href={updateVisibilityLink}>{children}</TextLink>
                  ),
                },
              )}
            </Text>

            <Box>
              <ButtonLink href={searchLink} role="link">
                {t('SEARCH_PEOPLE')}
              </ButtonLink>
            </Box>
          </Stack>
        </Box>
      </Box>
    </>
  );
};
